import React, { createContext, useContext, useEffect, useState } from 'react';
import { Zap } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { toast } from 'react-hot-toast';
import PremiumUpgradeModal from './PremiumUpgradeModal';

// Premium features configuration
const PREMIUM_FEATURES = {
    masteryFilters: {
      name: 'Mastery Filters',
      description: 'Filter topics by mastery level and track your learning progress',
      upgradeMessage: 'Upgrade to unlock mastery tracking across your workspaces'
    },
    advancedAnalytics: {
      name: 'Advanced Analytics',
      description: 'Deep insights into your learning patterns and progress',
      upgradeMessage: 'Upgrade to unlock detailed learning analytics'
    }
  };
  
  const PremiumContext = createContext(null);
  
  // Main provider component for premium features
  export const PremiumProvider = ({ children }) => {
    // State for user profile data, loading state, and any errors
    const [userProfile, setUserProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
    // Effect to fetch and sync user profile data
    useEffect(() => {
      // Function to fetch user profile from Supabase
      const fetchUserProfile = async () => {
        try {
          // Get current authenticated user
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            setIsLoading(false);
            return;
          }
  
          // Try to fetch existing user profile
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('*, subscription_status, subscription_end_date, subscription_tier')
            .eq('id', user.id)
            .single();
  
          if (error) {
            // If profile doesn't exist (PGRST116 error), create a new one
            if (error.code === 'PGRST116') {
              const { data: newProfile, error: createError } = await supabase
                .from('user_profiles')
                .insert([
                  { 
                    id: user.id,
                    display_name: user.email?.split('@')[0] || 'User',
                    subscription_status: 'free',
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  }
                ])
                .select()
                .single();
  
              if (createError) throw createError;
              setUserProfile(newProfile);
            } else {
              throw error;
            }
          } else {
            setUserProfile(profile);
          }
        } catch (error) {
          setError(error.message);
          toast.error('Error loading user profile');
        } finally {
          setIsLoading(false);
        }
      };
  
      fetchUserProfile();
  
      // Subscribe to auth state changes to keep profile in sync
      const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
        fetchUserProfile();
      });
  
      // Cleanup subscription on unmount
      return () => subscription.unsubscribe();
    }, []);
  
    // Add this new function
    const checkSubscriptionStatus = async () => {
      if (!userProfile?.id) return;
      
      setIsCheckingStatus(true);
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('subscription_status, subscription_end_date, subscription_tier')
          .eq('id', userProfile.id)
          .single();
          
        if (error) throw error;
        
        if (data) {
          setUserProfile(prev => ({
            ...prev,
            ...data
          }));
        }
      } catch (error) {
        // Handle error silently
      } finally {
        setIsCheckingStatus(false);
      }
    };
  
    // Add this effect to check status on workspace page load
    useEffect(() => {
      if (window.location.pathname === '/workspace') {
        checkSubscriptionStatus();
      }
    }, [window.location.pathname]);
  
    // Simplify isPremium check to only check for 'premium' status
    const isPremium = () => {
      if (!userProfile) {
        return false;
      }
      
      const isValidSubscription = userProfile.subscription_end_date 
        ? new Date(userProfile.subscription_end_date) > new Date() 
        : false;
        
      const premium = userProfile.subscription_status === 'premium' && isValidSubscription;
  
      return premium;
    };
  
    // Check if user has access to a specific feature
    const hasFeatureAccess = (featureName) => {
      if (isPremium()) return true;
      return false;
    };
  
    // Remove trial-related functions
    const getSubscriptionText = () => {
      if (!userProfile) return '';
      if (userProfile.subscription_status === 'premium' && 
          new Date(userProfile.subscription_end_date) > new Date()) {
        return `Premium (${userProfile.subscription_tier.replace('_', ' ')})`;
      }
      return '';
    };
    // Simplify openUpgradeModal to just redirect to pricing
    const openUpgradeModal = () => {
      setShowUpgradeModal(true);
    };
  
    const closeUpgradeModal = () => {
      setShowUpgradeModal(false);
    };
  
    // Component to wrap premium features with access control
    const PremiumFeature = ({ 
      feature, 
      children, 
      showUpgradeCard = true,
      className = "" 
    }) => {
      const hasAccess = hasFeatureAccess(feature);
      const featureConfig = PREMIUM_FEATURES[feature];
  
      // Hide completely if no access and showUpgradeCard is false
      if (!hasAccess && !showUpgradeCard) {
        return null;
      }
  
      // Show upgrade card if no access
      if (!hasAccess) {
        return (
          <div className={`relative rounded-lg border border-gray-200 ${className}`}>
            <div className="absolute inset-0 bg-gray-100/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="text-center p-4">
                <Zap className="w-8 h-8 mx-auto mb-2 text-amber-400" />
                <h3 className="text-lg font-semibold mb-2">{featureConfig.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{featureConfig.upgradeMessage}</p>
                <button
                  onClick={openUpgradeModal}
                  className="bg-cranberry-700 text-white px-4 py-2 rounded-lg hover:bg-cranberry-600 transition-colors"
                >
                  Upgrade to Premium
                </button>
              </div>
            </div>
            {children}
          </div>
        );
      }
  
      return children;
    };
  
    // Log feature access attempts for analytics
    const logFeatureAccess = async (featureName, success) => {
      try {
        await supabase
          .from('feature_access_history')
          .insert({
            user_id: userProfile.id,
            feature_name: featureName,
            success,
            access_type: isPremium() ? 'premium' : 'free',
            accessed_at: new Date().toISOString()
          });
      } catch (error) {
        console.error('Error logging feature access:', error);
      }
    };
  
    // Context value containing all premium-related data and functions
    const value = {
      isLoading,
      error,
      userProfile,
      isPremium,
      hasFeatureAccess,
      PremiumFeature,
      logFeatureAccess,
      PREMIUM_FEATURES,
      getSubscriptionText,
      openUpgradeModal,
      isCheckingStatus,
      checkSubscriptionStatus
    };
  
    // Show nothing while loading
    if (isLoading) {
      return null;
    }
  
    // Provide premium context to children components
    return (
      <PremiumContext.Provider value={value}>
        {children}
        <PremiumUpgradeModal 
          isOpen={showUpgradeModal} 
          onClose={closeUpgradeModal} 
        />
      </PremiumContext.Provider>
    );
  };
  export const usePremium = () => {
    const context = useContext(PremiumContext);
    if (context === undefined) {
      throw new Error('usePremium must be used within a PremiumProvider');
    }
    return context;
  };